// One-tap Favourites quick-pick for the time loggers. Presentational: the parent
// owns the form state and passes an onPick that pre-fills type + target, so you
// then only confirm the hours. Labels resolve from the task/SOW/overhead lists
// the logger already holds; a favourite that can't be resolved (e.g. an archived
// project not in the active list) is simply not shown.
const KIND_ICON = { task: '📋', sow: '📁', overhead: '🏷️' };
const KIND_NAME = { task: 'Task', sow: 'Project', overhead: 'Overhead' };

// Prefer resolving from the loaded lists (freshest name); otherwise fall back to
// the label stored on the favourite, so a pin always renders even when its item
// isn't in the current list (e.g. a task not in your "mine" scope).
function labelFor(fav, { tasks, sows, overheads }) {
  if (fav.kind === 'task') {
    const t = (tasks || []).find(x => String(x.id) === fav.ref_id);
    if (t) return { label: t.name, sub: t.project_name || 'Task' };
  } else if (fav.kind === 'sow') {
    const s = (sows || []).find(x => String(x.id) === fav.ref_id);
    if (s) return { label: s.reference || s.title || 'Project', sub: s.client_name || s.title || 'Project' };
  } else {
    const o = (overheads || []).find(x => String(x.id) === fav.ref_id);
    if (o) {
      const parent = (overheads || []).find(x => String(x.id) === String(o.parent_id));
      return { label: o.name, sub: parent ? parent.name : 'Overhead' };
    }
  }
  return fav.label ? { label: fav.label, sub: KIND_NAME[fav.kind] || '' } : null;
}

export default function FavouritesBar({
  favourites = [], tasks, sows, overheads,
  onPick, onRemove, current, isCurrentFav, onToggleCurrent,
  theme = 'blue',
}) {
  const accent = theme === 'navy' ? 'text-navy border-navy' : 'text-blue-700 border-blue-300';
  const resolved = favourites
    .map(f => ({ fav: f, meta: labelFor(f, { tasks, sows, overheads }) }))
    .filter(x => x.meta);

  const canFavCurrent = current && current.ref_id && onToggleCurrent;
  if (resolved.length === 0 && !canFavCurrent) return null;

  return (
    <div className="mb-1">
      <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">★ Favourites</div>
      <div className="flex flex-wrap gap-1">
        {resolved.map(({ fav, meta }) => (
          <span key={`${fav.kind}:${fav.ref_id}`}
            className={`group inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full border bg-white text-[10px] font-semibold ${accent} hover:bg-gray-50`}>
            <button type="button" onClick={() => onPick(fav)} title={`${meta.label}${meta.sub ? ' · ' + meta.sub : ''}`} className="inline-flex items-center gap-1 max-w-[150px]">
              <span>{KIND_ICON[fav.kind]}</span>
              <span className="truncate">{meta.label}</span>
            </button>
            {onRemove && (
              <button type="button" onClick={() => onRemove(fav)} title="Remove favourite"
                className="text-gray-300 hover:text-red-500 leading-none">×</button>
            )}
          </span>
        ))}
        {canFavCurrent && (
          <button type="button" onClick={onToggleCurrent}
            title={isCurrentFav ? 'Remove from favourites' : 'Add current selection to favourites'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${isCurrentFav ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-dashed border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
            {isCurrentFav ? '★ Favourited' : '☆ Favourite this'}
          </button>
        )}
      </div>
    </div>
  );
}
